import { useEffect } from "react";

// Define the type for the refs and the hook props for clarity
type RefObject<T> = React.RefObject<T>;

/**
 * Custom hook to detect clicks outside of a set of elements (specified by refs).
 * @param refs An array of React ref objects pointing to the elements to watch.
 * @param callback The function to execute when a click outside the elements occurs.
 * @param isDropdownOpen A boolean state that determines if the outside listener should be active.
 */
const useOutsideAlerter = (
  refs: RefObject<HTMLElement>[],
  callback: () => void,
  isDropdownOpen: boolean,
) => {
  useEffect(() => {
    // Only attach the listener if the dropdown is open (isDropdownOpen is true)
    if (!isDropdownOpen) return;

    function handleClickOutside(event: MouseEvent) {
      // Check if the click target is outside ALL of the provided refs
      const isOutside = refs.every(
        (ref) => ref.current && !ref.current.contains(event.target as Node),
      );

      // Also check if the clicked element is an IonFabButton, to allow FAB actions
      const isFabClicked = (event.target as HTMLElement).closest(
        "ion-fab-button",
      );

      // If the click is outside AND not on a fab button, run the callback
      if (isOutside && !isFabClicked) {
        callback();
      }
    }

    // Add the event listener when the component mounts or dependencies change
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener when the component unmounts or dependencies change
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [refs, callback, isDropdownOpen]);
};

export default useOutsideAlerter;
